import { LessonInput, LessonItemFragment } from '../../../types/planung-graphql-client-defs';
import { isNotEmpty } from '../../../utils/typeguards';
import { LessonTableType } from '../TimetableVersion/Table/TimetableVersionLessonsTable';
import { LessonFormType, VersionLessonFormType } from '../types';

type LessonFormParts = Pick<
  LessonFormType,
  | 'subject'
  | 'subTitle'
  | 'comment'
  | 'studentCount'
  | 'elective'
  | 'isClassTrip'
  | 'teachingLoadEnabled'
  | 'timetableEnabled'
  | 'teachers'
  | 'lessonClasses'
  | 'lessonUnit'
  | 'roomSupply'
>;

/**
 * Vollständiger Stand der Zuordnung aus den Formularwerten; die Reihenfolge der Listen ist ihre
 * Reihenfolge. Lehrer-Zeilen ohne gewählte Person und Räume ohne uuid werden nicht gespeichert.
 */
export function toLessonInput(values: LessonFormParts | VersionLessonFormType): LessonInput {
  const lessonUnits = 'lessonUnit' in values ? values.lessonUnit : undefined;
  const studentCount = 'studentCount' in values ? values.studentCount : undefined;

  return {
    subjectUuid: values.subject?.uuid ?? '',
    subTitle: values.subTitle,
    comment: values.comment,
    studentCount: studentCount != null ? Number(studentCount) : null,
    teachingLoadEnabled: values.teachingLoadEnabled ?? false,
    timetableEnabled: values.timetableEnabled ?? false,
    elective: values.elective ?? false,
    isClassTrip: values.isClassTrip ?? false,
    lessonClasses:
      values.lessonClasses?.map((lessonClass) => ({
        classUuid: lessonClass.class.uuid,
        groupUuids: lessonClass.groups?.map((group) => group.uuid) ?? [],
      })) ?? [],
    teachers:
      values.teachers?.flatMap((teacher) =>
        teacher.person?.uuid
          ? [
              {
                personUuid: teacher.person.uuid,
                description: teacher.description,
                present: teacher.present,
                teachingLoad: teacher.teachingLoad,
                teachingLoadHours: teacher.teachingLoadHours,
                writesCertificate: teacher.writesCertificate,
              },
            ]
          : [],
      ) ?? [],
    lessonUnits:
      lessonUnits?.map((lessonUnit) => ({
        count: lessonUnit.count != null ? Number(lessonUnit.count) : null,
        duration: lessonUnit.duration != null ? Number(lessonUnit.duration) : null,
        subjectContainerUuid: lessonUnit.subjectContainerUuid || null,
      })) ?? [],
    roomSupply:
      values.roomSupply?.flatMap((room) =>
        room.uuid ? [{ roomUuid: room.uuid, descriptions: room.description }] : [],
      ) ?? [],
  };
}

/**
 * Epochenplan-Zuordnungen, die zu den Einheiten der Zuordnung noch fehlen: je Schiene und Klasse eine
 * Zuordnung direkt an der Schiene, sofern es sie nicht schon gibt.
 */
export function buildEpochPlanInputs(values: LessonFormType, existingLessonsData: LessonTableType[]): LessonInput[] {
  const subjectContainerUuids = values.lessonUnit?.map((lu) => lu.subjectContainerUuid).filter(isNotEmpty);
  const classUuids = values.lessonClasses?.map((lc) => lc.class.uuid).filter(isNotEmpty);

  if (!subjectContainerUuids?.length || !classUuids?.length) {
    return [];
  }

  const candidates = subjectContainerUuids.flatMap((subjectContainerUuid) =>
    classUuids.map((classUuid) => ({ classUuid, subjectContainerUuid })),
  );

  // Filter out already existing epoch plans
  const filtered = candidates.filter(
    (item) =>
      !existingLessonsData.some(
        (ld) =>
          item.subjectContainerUuid === ld.subjectUuid &&
          ld.classes.length === 1 &&
          item.classUuid === ld.classes[0].uuid,
      ),
  );

  // De-duplicate by composite key (subjectContainerUuid + classUuid) - Bug-Fix #2
  const seen = new Set<string>();
  const unique = filtered.filter((item) => {
    const key = `${item.subjectContainerUuid}::${item.classUuid}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const basics = toLessonInput({ ...values, lessonClasses: [], teachers: [], lessonUnit: [], roomSupply: [] });

  return unique.map((item) => ({
    ...basics,
    subjectUuid: item.subjectContainerUuid,
    lessonClasses: [{ classUuid: item.classUuid, groupUuids: [] }],
  }));
}

/** Formularwerte einer bestehenden Zuordnung, wie sie Bearbeiten und Duplizieren brauchen. */
export function toLessonFormValues(lesson: LessonItemFragment): LessonFormType {
  return {
    uuid: lesson.uuid,
    studentCount: lesson.studentCount,
    curriculumUuid: lesson.curriculumUuid ?? undefined,
    subject: { uuid: lesson.subject.uuid, name: lesson.subject.name },
    subTitle: lesson.subTitle,
    comment: lesson.comment,
    elective: lesson.elective,
    isClassTrip: lesson.isClassTrip,
    teachingLoadEnabled: lesson.teachingLoadEnabled,
    timetableEnabled: lesson.timetableEnabled,
    onlyInTimetableVersion: lesson.onlyInTimetableVersion,
    lessonClasses: lesson.lessonClasses.map((lessonClass) => ({
      class: {
        uuid: lessonClass.class.uuid,
        name: lessonClass.class.name,
        color: lessonClass.class.color,
        grade: lessonClass.class.grade,
      },
      uuid: lessonClass.uuid,
      groups: lessonClass.groups.map((group) => ({ uuid: group.uuid, name: group.name })),
    })),
    roomSupply: lesson.roomSupply.map((room) => ({
      uuid: room.uuid,
      description: room.descriptions ?? '',
    })),
    teachers: lesson.teachers.map((teacher) => ({
      present: teacher.present,
      teachingLoad: teacher.teachingLoad,
      teachingLoadHours: teacher.teachingLoadHours,
      description: teacher.description,
      writesCertificate: teacher.writesCertificate,
      person: {
        uuid: teacher.uuid,
        fullName: teacher.fullName,
        displayNameShort: teacher.displayNameShort || teacher.fullName,
        color: teacher.color,
      },
    })),
    lessonUnit: lesson.lessonUnits.map((unit) => ({
      blocked: false,
      duration: unit.duration,
      count: unit.count,
      subjectContainerUuid: unit.subjectContainer?.uuid,
      lessonUnitType: unit.subjectContainer?.uuid ? 'epoch' : 'subjectHour',
    })),
  };
}

/** Eine Kopie entsteht ohne uuid und ohne Bezug zur Sollstunde. */
export function buildDuplicateLessonFormValues(lesson: LessonItemFragment): LessonFormType {
  return { ...toLessonFormValues(lesson), uuid: undefined, curriculumUuid: undefined };
}

type CardSource = {
  durationCountArray?: Array<{ duration?: number | null; count?: number | null }>;
  lessonUnit?: Array<{ duration?: number | string | null; count?: number | string | null }>;
};

/**
 * Dauern der Karten, die mit einer Zuordnung in einer Version entstehen: je Eintrag eine Karte.
 * Gezählt wird aus der Karten-Angabe des Formulars, sonst aus den Einheiten.
 */
export function toCardDurations(values: CardSource): number[] {
  const source = values.durationCountArray ?? values.lessonUnit ?? [];
  return source.flatMap((entry) => {
    const count = Math.abs(Number(entry.count ?? 0));
    const duration = entry.duration != null ? Number(entry.duration) : 1;
    return Array.from({ length: count }, () => duration || 1);
  });
}
